<script setup lang="ts">
import { useUserModeChange } from "@/store/useUserModeChange";
import { getAreaLabel } from "@/utils/areaNaming";
import { getBookingOptionQuantity } from "@/utils/bookingOptionQuantities";
import { formatDurationLabel } from "@/utils/BookingStepperUtil";
import { formatPrice } from "@/utils/Formatting";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { ActivitiesRecord } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";
import { useRouter } from "vue-router";
import { toast } from "vue3-toastify";

const getSetupStore = useGlobalSetupStore();
const { globalSetup } = storeToRefs(getSetupStore);
const { copy, copied } = useClipboard()
const userMode = useUserModeChange();
const { isUserMode } = storeToRefs(userMode);
const router = useRouter();

const props = defineProps<{
  booking: any;
  activity: ActivitiesRecord;
  areaList: any[];
  memberList: any[];
  role: string;
  products: any[];
  canMessage: boolean;
  haveEditAccess: boolean;
  haveCreateAccess: boolean;
  debugUser: any;
}>();

//Data
const showMessageDialog = ref(false)
const isHistoryDialogOpen = ref(false)

// Computed
const bay = computed(() => {
  const area = props.areaList?.find((area) => area.id === props.booking?.area);
  return area ? getAreaLabel(area, props.activity, globalSetup.value?.areaSingular || "Bay") : `${globalSetup.value?.areaSingular || "Bay"} `;
});

const members = computed(() => {
  return props?.memberList?.filter((user) => props.booking?.users?.includes(user.id))
});

const startDate = computed(() => {
  const sd = props.booking?.startDate;

  return sd ? moment(sd).isSame(moment(), "day")
    ? `Today ${moment(sd).format("h:mm A")}`
    : `${moment(sd).format("dddd DD-MM-YY h:mm A")}`
    : "Unknown Start Time";
});

const createdDate = computed(() => {
  const created = props.booking?.created;

  return created ? moment(created).isSame(moment(), "day")
    ? `Today ${moment(created).format("h:mm A")}`
    : `${moment(created).format("dddd DD-MM-YY h:mm A")}`
    : "Unknown Booked Time";
})

const createdBy = computed(() => {
  return props?.memberList
    ?.filter((user) => props.booking?.createdBy === user.id)
    .map((user) => `${user.firstName} ${user.lastName}`)
})

// Methods
const messageBooking = () => {
  showMessageDialog.value = true
}

const openUserProfile = (userId: string) => {
  if (!userId || isUserMode.value) return;
  router.push(`/user/${userId}`);
};

const doBookingTime = (mins:number) => {
  return formatDurationLabel(mins, true)
}

</script>
<template>
  <VCardText class="text-center">
    <div class="d-flex justify-space-between topButtons">
      <p class="mb-1">{{ props.booking.id }}</p>
      <div class="d-flex ga-2">
        <v-btn
          v-if="(props.role === 'Admin' || props.haveEditAccess || props.haveCreateAccess) && props?.booking?.id"
          @click.prevent="isHistoryDialogOpen = true"
          variant="tonal"
          size="small"
        >
          <v-icon icon="mdi-history" />
        </v-btn>
        <v-btn
          v-if="props.canMessage && props?.booking?.id && props?.booking?.id != ''"
          @click.prevent="messageBooking()"
          variant="tonal"
          size="small"
        >
          <v-icon icon="mdi-message" />
        </v-btn>
      </div>
    </div>
    <VContainer>
      <div class="d-flex align-center justify-space-between position-relative">
      <p class="text-h6 mb-1 ms-auto me-auto">
        {{ bay }}
        {{
          props.booking.source === "Imported"
            ? " Imported From: " + props.booking.extSource
            : props.booking.source
        }}
        <v-btn
          variant="plain"
          :color="copied ? 'green' : ''"
          icon="mdi-content-copy"
          size="small"
          @click="() => {
            copy(props.booking.id);
            toast.success('Booking ID saved to clipboard', {
              position: toast.POSITION.BOTTOM_RIGHT,
              autoClose: 2000,
            });
          }"
          v-if="props.role === 'Admin' && !isUserMode && props.debugUser"
        />
      </p>
      </div>

      <div class="text-h6 mb-1">
        {{
          props.booking.referrer
            ? "Referrer: " + props.booking.referrer
            : ""
        }}
      </div>
      <div class="text-h6 mb-1">
        {{ startDate }} for
        <span v-html="doBookingTime(props.booking.duration - (props.activity?.bookingGap ? Math.floor(props.activity.bookingGap)  : 0 ))"></span>
      </div>
      <div class="text-h6 mb-1 d-flex flex-wrap justify-center align-center ga-2" v-if="members?.length">
        <span>Members:</span>
        <VChip
          v-for="user in members"
          :key="user.id"
          :disabled="isUserMode"
          color="primary"
          size="small"
          @click="openUserProfile(user.id)"
        >
          {{ `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.username }}
        </VChip>
      </div>
      <div class="text-h6 mb-1" v-if="props.booking.visitors">
        Visitors: {{ props.booking.visitors || "None" }}
      </div>
      <div class="text-h6 mb-1">
        Contact:
        {{
          members?.length
            ? `${members[0]?.email ?? ''} ${members[0]?.mobileNumber ?? ''}`.trim()
            : props?.booking?.visitorEmail || props?.booking?.visitorPhone
              ? `${props?.booking?.visitorEmail} ${props?.booking?.visitorPhone}`
              : "None given"
        }}
      </div>

      <div>
        Booked
        {{
          !props?.booking?.createdBy
            ? ` By Guest Online ${createdDate}`
            : ` By ${createdBy} ${createdDate}`
        }}
      </div>
      <div>
        Attendees {{props.booking.numberInParty}}
      </div>

      <p v-if='props?.booking?.willRemoveEmailSent && (props.role === "Admin" || props.haveEditAccess || props.haveCreateAccess)' class="mt-1">
        Will remove email sent on {{ moment(props?.booking?.willRemoveEmailSent).format('DD-MM-YYYY hh:mm A') }}
      </p>

      <VCol v-if="props.products?.length > 0" cols="12">
        <div>
          <div class="d-flex flex-wrap gap-4">
            <VCheckbox
              v-for="product in props.products.filter((p:any) => p?.name && p?.id)"
              :key="product.id"
              :label="`${product.name} (Qty: ${getBookingOptionQuantity(props.booking?.optionQuantities, product.id)})`"
              :model-value="true"
              readonly
              color="primary"
              density="compact"
            />
          </div>
        </div>
      </VCol>
    </VContainer>
    <VDivider v-if="!props?.booking?.notes || props?.booking?.notes == ''"/>
    <v-alert class="rounded overflow-auto" style="max-height: 100px;" v-if="props?.booking?.notes && props?.booking?.notes != ''">
      <h3>Booking Notes:</h3>
      {{props.booking.notes}}
    </v-alert>
    <div v-if="props?.booking?.actualStartTime">
      Actual Times &nbsp; &nbsp; &nbsp; Start:
      {{ moment(props?.booking?.actualStartTime).format("h:mm A") }}

      &nbsp; &nbsp; &nbsp; End:
      {{ moment(props?.booking?.actualStartTime).add(props.booking.actualDuration, "minutes").format("h:mm a") }}
    </div>
    <div
      v-if="!props?.booking?.actualStartTime && props.booking?.state == 'Confirmed' && moment(props.booking?.startDate).isBefore(moment())"
      class="mt-2"
    >
      Booking was not started!
    </div>
    <div
      v-if="props?.booking?.paid > 0 && props.booking?.due > 0"
      class="mt-2"
    >
      Partial payment of {{ formatPrice(props?.booking?.paid) }}
    </div>
  </VCardText>
  <email-s-m-s
    v-if="showMessageDialog"
    :booking-id="props.booking.id"
    @close-dialog="showMessageDialog = false"
  />
  <BookingHistoryDialog
    :is-open="isHistoryDialogOpen"
    :booking-id="props.booking.id"
    @close-dialog="isHistoryDialogOpen = false"
  />
</template>
<style scoped>
.topButtons {
  margin-top: -12px;
  margin-left: -8px;
  margin-bottom: 6px;
}
</style>
